import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Flag } from 'lucide-react';
import { apiFetch, v1Path } from '../../lib/api';
import { listRelations } from '../../lib/relations';
import { cn } from '../../lib/utils';
import type { ProjectDto, StateDto, WorkItemFull, WorkItemRef } from '../../lib/work-types';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

export type DeliveryHandoff = { projectId: string; readyStateId: string | null };

type Department = {
  projectId: string;
  streamName: string;
  task: WorkItemFull;
  states: StateDto[];
  readyStateId: string | null;
};

function personLabel(ref: WorkItemRef['assignees'][number]): string {
  return [ref.first_name, ref.last_name].filter(Boolean).join(' ') || ref.email || 'Someone';
}

async function loadDepartments(
  requestProjectId: string,
  requestId: string,
  taskIds: string[],
  handoffs: DeliveryHandoff[],
): Promise<Department[]> {
  const [relations, projects] = await Promise.all([
    listRelations(requestProjectId, requestId),
    apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0')),
  ]);
  const ids = new Set(taskIds);
  const ends = relations
    .map((relation) => (relation.from.id === requestId ? relation.to : relation.from))
    .filter((end) => ids.has(end.id));
  return Promise.all(ends.map(async (end) => {
    const [task, states] = await Promise.all([
      apiFetch<WorkItemFull>(v1Path(`/projects/${end.project_id}/work-items/${end.id}`)),
      apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${end.project_id}/states`)),
    ]);
    return {
      projectId: end.project_id,
      streamName: projects.items.find((project) => project.id === end.project_id)?.name ?? end.project_identifier,
      task,
      states: states.items
        .filter((state) => state.group !== 'cancelled')
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
      readyStateId: handoffs.find((handoff) => handoff.projectId === end.project_id)?.readyStateId ?? null,
    };
  }));
}

function reachedFinish(department: Department): boolean {
  if (!department.readyStateId) return false;
  const index = (id: string) => department.states.findIndex((state) => state.id === id);
  const at = index(department.task.state_id);
  const finish = index(department.readyStateId);
  return at >= 0 && finish >= 0 && at >= finish;
}

/**
 * Live view of the departments an approved request was handed to: where each department's
 * task sits in its own stream, and every subtask with its status and owner.
 */
export default function DeliveryPanel({
  requestProjectId,
  requestId,
  taskIds,
  handoffs,
}: {
  requestProjectId: string;
  requestId: string;
  taskIds: string[];
  handoffs: DeliveryHandoff[];
}) {
  const [departments, setDepartments] = useState<Department[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const idsKey = taskIds.join(',');

  useEffect(() => {
    let cancelled = false;
    setError(null);
    void loadDepartments(requestProjectId, requestId, idsKey ? idsKey.split(',') : [], handoffs)
      .then((rows) => { if (!cancelled) setDepartments(rows); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load delivery status'); });
    return () => { cancelled = true; };
    // handoffs come from the request type and only change with it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestProjectId, requestId, idsKey]);

  const gated = (departments ?? []).filter((department) => department.readyStateId);
  const done = gated.filter(reachedFinish).length;

  return (
    <section className="space-y-3" aria-label="Delivery">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold">Delivery</h2>
        {departments && gated.length ? (
          <span className="text-xs text-muted-foreground">{done} of {gated.length} department{gated.length === 1 ? '' : 's'} done</span>
        ) : null}
      </div>
      {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
      {!departments && !error ? <p className="text-sm text-muted-foreground">Loading delivery status…</p> : null}
      {departments?.length === 0 ? <p className="text-sm text-muted-foreground">No department tasks were created for this request.</p> : null}
      {departments?.map((department) => {
        const currentIndex = department.states.findIndex((state) => state.id === department.task.state_id);
        const current = department.states[currentIndex];
        const finished = reachedFinish(department);
        const subtasks = department.task.children ?? [];
        const closedSubtasks = subtasks.filter((child) => child.state.group === 'completed').length;
        return (
          <Card key={department.projectId} className={cn(finished && 'border-emerald-200 dark:border-emerald-900')}>
            <CardHeader className="flex-row items-center justify-between gap-3 space-y-0 pb-3">
              <div className="min-w-0">
                <CardTitle className="text-base">{department.streamName}</CardTitle>
                <Link
                  to={`/projects/${department.projectId}/tasks?peek=${encodeURIComponent(department.task.key)}`}
                  className="text-xs text-primary hover:underline"
                >
                  {department.task.key} · {department.task.title}
                </Link>
              </div>
              <span
                className={cn(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
                  finished ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800',
                )}
              >
                {finished ? <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> : null}
                {current?.name ?? 'Unknown column'}
              </span>
            </CardHeader>
            <CardContent className="space-y-4">
              <ol className="flex flex-wrap items-center gap-1" aria-label={`${department.streamName} progress`}>
                {department.states.map((state, index) => {
                  const reached = currentIndex >= 0 && index <= currentIndex;
                  const isFinish = state.id === department.readyStateId;
                  return (
                    <li key={state.id} className="flex items-center gap-1">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px]',
                          index === currentIndex ? 'bg-blue-600 font-semibold text-white'
                            : reached ? 'bg-blue-100 text-blue-800' : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {isFinish ? <Flag className="h-3 w-3" aria-label="Finish column" /> : null}
                        {state.name}
                      </span>
                      {index < department.states.length - 1 ? <span className="text-muted-foreground" aria-hidden>›</span> : null}
                    </li>
                  );
                })}
              </ol>
              {subtasks.length ? (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">Tasks · {closedSubtasks} of {subtasks.length} done</p>
                  <ul className="divide-y rounded-lg border">
                    {subtasks.map((child) => (
                      <li key={child.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                        {child.state.group === 'completed'
                          ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden />
                          : <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: child.state.color }} aria-hidden />}
                        <span className="min-w-0 flex-1 truncate">{child.title}</span>
                        <span className="text-xs text-muted-foreground">{child.state.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {child.assignees.length ? child.assignees.map(personLabel).join(', ') : 'Unassigned'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No subtasks.</p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </section>
  );
}
